import { useCallback, useEffect, useState } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'

interface HolidayItem {
  date: string
  name: string
  type: string
}

export type HolidayStatus = 'idle' | 'loading' | 'success' | 'error'

export interface HolidayState {
  /** 비작업일 날짜(YYYY-MM-DD) 집합 — status가 'success'일 때만 유효. 그 외에는 빈 집합이며 계산에 쓰면 안 된다. */
  dates: Set<string>
  status: HolidayStatus
  /** 성공한 조회의 범위(YYYY-MM-DD). 성공 전에는 null. 이 범위 밖 날짜는 작업일 계산 금지. */
  from: string | null
  to: string | null
  /** 날짜 이동·저장을 막아야 하는 상태(조회 중·실패·범위 미지정). */
  blocked: boolean
  /** 실패 후 다시 조회 */
  retry: () => void
}

/**
 * BE `GET /schedule/holidays?from&to` 조회 — 비작업일(SUN/PUBLIC/SUBSTITUTE) 날짜 집합과
 * 조회 상태·범위를 반환한다. 실패해도 빈 집합을 정상값처럼 쓰지 않도록 status·blocked로 알린다
 * (A-08). 호출측은 blocked일 때 계산·저장을 막고 오류 안내와 retry를 제공한다.
 * 조회 범위는 공사 기간 기준으로 넉넉히 잡아 전달할 것(scheduleUtils.holidayQueryRange).
 * 회원 전용 엔드포인트(OWNER/STAFF)이므로 외부공유 뷰어(GUEST)에서는 쓸 수 없다 —
 * 그 화면은 ShareHistoryService#getShareView가 응답에 함께 내려주는 holidays[]를 대신 쓴다.
 */
export function useHolidayRange(fromDate?: string, toDate?: string): HolidayState {
  const user = useAuthStore(s => s.user)
  const [state, setState] = useState<{ dates: Set<string>; status: HolidayStatus; from: string | null; to: string | null }>(
    { dates: new Set(), status: 'idle', from: null, to: null },
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!fromDate || !toDate) {
      setState({ dates: new Set(), status: 'idle', from: null, to: null })
      return
    }
    const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    let cancelled = false
    setState({ dates: new Set(), status: 'loading', from: null, to: null })
    apiClient
      .get<{ data: HolidayItem[] }>('/schedule/holidays', {
        params: { from: fromDate, to: toDate },
        headers,
      })
      .then(res => {
        if (cancelled) return
        setState({
          dates: new Set((res.data.data ?? []).map(h => h.date)),
          status: 'success', from: fromDate, to: toDate,
        })
      })
      .catch(() => {
        if (!cancelled) setState({ dates: new Set(), status: 'error', from: null, to: null })
      })
    return () => { cancelled = true }
  }, [fromDate, toDate, user?.compId, attempt])

  const retry = useCallback(() => setAttempt(n => n + 1), [])

  return { ...state, blocked: state.status !== 'success', retry }
}

/**
 * 날짜 집합만 필요한 기존 호출처(캘린더·리스트·추출 화면)용. 조회 중·실패 시 빈 집합을 반환하므로
 * 날짜 이동·저장 계산에는 쓰지 말고 useHolidayRange를 쓴다.
 */
export function useHolidays(fromDate?: string, toDate?: string): Set<string> {
  return useHolidayRange(fromDate, toDate).dates
}
